import 'vuetify/styles';
import '@mdi/font/css/materialdesignicons.css';
import {createVuetify, type ThemeDefinition} from 'vuetify';
import {VBtn} from 'vuetify/components';

import {white, orange, blueGray, navy, red, yellow, green, linkBlue, lightGray} from '@/lib/colors';

/**
 * Brand palette → Vuetify theme tokens. Add a token here once, and it becomes
 * available both as a `color="..."` prop on Vuetify components and as a
 * `--v-theme-*` CSS custom property usable from SCSS via `rgb(var(--v-theme-*))`.
 */
const npVuetifyTheme: ThemeDefinition = {
  dark: false,
  colors: {
    'background': white,
    'surface': white,
    'on-surface': navy, // #2c3e50 ($darkNavy)
    'primary': orange, // #e18d3d (npOrange)
    'secondary': blueGray, // #405866 (npGray)
    'info': linkBlue, // #009ddc ($linkBlue)
    'success': green, // #297d0a
    'warning': yellow, // #eed75a
    'error': red, // #eb361d,
    'light-gray': lightGray, // #eeeeee (npLightGray)
  },
};

/**
 * The standard NorthPoint button as a Vuetify alias — a `VBtn` preset to the filled
 * secondary (npGray) look. It stays fully native (no wrapper component re-implementing
 * Vuetify's API): use it in templates as <np-action-btn>, and every prop is still
 * override-able per instance.
 *
 * It is the explicit, opt-in replacement for what used to be a global `VBtn` default.
 * Convention: button *typography* is global (see sass/settings.scss + sass/global.scss);
 * button *color/variant* is always explicit — either via <np-action-btn> for the standard
 * solid button, or set directly on a plain `<v-btn>` for other styles (outlined, text,
 * icon). There is intentionally no global `VBtn` color/variant default, so a bare
 * `<v-btn>` never silently inherits a brand style.
 *
 * Exported so the unit-test Vuetify factory (tests/utilities/vuetify.ts) registers the
 * same alias — keeps one source of truth for button styling.
 */
export const buttonAliases = {
  NpActionBtn: VBtn,
};

export const buttonAliasDefaults = {
  NpActionBtn: {color: 'secondary', variant: 'flat'},
};

export const vuetify = createVuetify({
  theme: {
    defaultTheme: 'npVuetifyTheme',
    themes: {npVuetifyTheme},
  },
  aliases: buttonAliases,
  defaults: buttonAliasDefaults,
});
